import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: 'medium' | 'large' | 'xlarge';
  hideHeader?: boolean;
  headerActions?: React.ReactNode;
}

const widthClasses = {
  medium: 'max-w-lg',
  large: 'max-w-2xl',
  xlarge: 'max-w-3xl',
};

export function SidePanel({
  isOpen,
  onClose,
  title,
  children,
  width = 'large',
  hideHeader = false,
  headerActions,
}: SidePanelProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/20" />
        </Transition.Child>

        {/* Panel Container - anchored to the right */}
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel
                  className={`pointer-events-auto w-screen ${widthClasses[width]}`}
                >
                  <div className="flex h-full flex-col bg-surface-default border-l border-border-subtle shadow-xl">
                    {/* Header */}
                    {!hideHeader && (
                      <div className="flex items-center justify-between border-b border-border-subtle px-6 py-4 flex-shrink-0">
                        <Dialog.Title className="text-lg font-semibold text-text-primary">
                          {title}
                        </Dialog.Title>
                        <div className="flex items-center gap-2">
                          {headerActions}
                          <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md p-1 text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Content - children control their own layout */}
                    {children}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// Footer pinned to the bottom of the panel
interface SidePanelFooterProps {
  children: React.ReactNode;
}

export function SidePanelFooter({ children }: SidePanelFooterProps) {
  return (
    <div className="flex items-center justify-end gap-3 border-t border-border-subtle px-6 py-4 flex-shrink-0 bg-surface-subtle">
      {children}
    </div>
  );
}
